export { setMarketingToggled3D } from '@/services/map-service/marketing'
export * from '@/services/map-service/types'

import { reactive } from 'vue'
import booleanContains from '@turf/boolean-contains'
import { point, polygon } from '@turf/turf'
import Geographic from '@/lib/geographic'
import Camera from '@/lib/camera'
import {
  getLayersByIdPrefix,
  layerManagementReady,
  hideLayers as hideStyleLayers,
  setLayerFilters as setStyleLayerFilters,
  setLayerOpacities as setStyleLayerOpacities,
  showLayers as showStyleLayers,
} from '@/services/layer-management-service'
import { registerEventsOnIdle } from '@/services/map-service/marketing'
import type {
  GeoPositionBounds,
  FitFeature,
  ImageParam,
  LegacyLayer,
  MapImage,
  Marker,
  OptionsParams,
  Popup,
} from '@/services/map-service/types'
import type MapFeature from '@/lib/map-feature'
import type { Feature, Polygon, Point } from 'geojson'
import type { FeatureSelector } from '@/lib/map-x/webmap-viewer-types'
import { SEARCH_PIN_SOURCE } from '@/lib/enums'

// ready promise
export let readyResolver: (value: boolean) => void
export let mapReadyPromise = new Promise<boolean>(
  (resolve) => (readyResolver = resolve)
)

// constants
const QUERY_CLICK_MODE = 'query'
const SELECT_MARKUP_CLICK_MODE = 'select-markup'
const SELECT_PARCEL_CLICK_MODE = 'select-parcel'
// Create polygon with same coordinate order as setVisibleRegion: [UL, UR, LR, LL, UL]
// bbox is [minLon, minLat, maxLon, maxLat]
const US_GEO_JSON = polygon([
  [
    // upper-left
    [-120.36516, 47.27362],
    // upper-right
    [-67.2897, 47.27362],
    // lower-right
    [-67.2897, 24.63105],
    // lower-left
    [-120.36516, 24.63105],
    // close polygon
    [-120.36516, 47.27362],
  ],
])

export const DEFAULT_HOME = new Geographic(-93.87, 45.36, 5)

interface State {
  isReady: boolean
  isIdle: boolean
  cursorCoord: Geographic | null
  camera: Camera | null
  destination: Camera | null
  elevationScrubMarker: Feature[]
  geolocationEnabled: boolean | null
  featuresInView: Record<string, MapFeature[]>
  featuresHovered: MapFeature[]
  featuresHighlighted: Feature[]
  featuresFiltered: MapFeature[]
  featuresFavorited: MapFeature[]
  featureSelected: FeatureSelector | null
  featureTargeted: FeatureSelector | null
  fitFeature: FitFeature | null
  fitBounds: GeoPositionBounds | null
  queryCoord: Geographic | null
  queryFeatures: MapFeature[]
  queryMarker: Feature[]
  quickDestination: Camera | null
  terrainExaggeration: number
  trailMarkerFeatures: Feature<Point>[]
  spotFeatures: Feature<Point>[]
  searchPinFeatures: Feature<Point>[]
  searchPinSelected: FeatureSelector | null
  toggledLayers: Record<string, LegacyLayer>
  userCoord: Geographic | null
  userZoom: {
    deltaZoom: number | null
  }
  viewer3d: boolean | null
  visibleRegion: Feature<Polygon>
  clickMode: string
  cursor: string
  canPitch: boolean
  canRotate: boolean
  popup: Popup | null
  markers: Marker[]
  images: Map<string, MapImage>
}

// reactive state
export const observableState = reactive<State>({
  isReady: false,
  isIdle: false,
  cursorCoord: null,
  camera: null,
  destination: null,
  elevationScrubMarker: [],
  geolocationEnabled: null,
  featuresInView: {},
  featuresHovered: [],
  featuresHighlighted: [],
  featuresFiltered: [],
  featuresFavorited: [],
  featureSelected: null,
  featureTargeted: null,
  fitFeature: null,
  fitBounds: null,
  queryCoord: null,
  queryFeatures: [],
  queryMarker: [],
  quickDestination: null,
  terrainExaggeration: 1,
  trailMarkerFeatures: [],
  spotFeatures: [],
  searchPinFeatures: [],
  searchPinSelected: null,
  toggledLayers: {},
  userCoord: null,
  userZoom: {
    deltaZoom: null,
  },
  viewer3d: null,
  visibleRegion: US_GEO_JSON,
  clickMode: QUERY_CLICK_MODE,
  cursor: 'default',
  canPitch: true,
  canRotate: true,
  popup: null,
  markers: [],
  images: new Map(),
})

/**
 * Set the ready state of the map viewer
 */
export const setReady = (bool: boolean) => {
  if (bool) {
    readyResolver(true)
    observableState.isReady = true
  } else {
    // If the map ready state is unset, create a reference to the old resolver.
    // This will ensure any old promise listeners won't get dropped.
    const oldResolver = readyResolver

    // Reassign map ready promise
    mapReadyPromise = new Promise((resolve) => (readyResolver = resolve))

    // Any old promises will resolve when `mapReadyPromise` is resolved.
    mapReadyPromise.then(oldResolver)

    observableState.isReady = false
  }
}

/**
 * The ready state of the map viewer
 * @returns ready state of map
 */
export const getReady = () => observableState.isReady

/**
 * Sets map idle on map load
 */
export const setIsIdle = (bool: boolean) => {
  observableState.isIdle = bool
}

/**
 * The initial idle state of the map.
 */
export const getIsIdle = () => observableState.isIdle

/**
 * Set the features for trail markers.  Takes variadic parameters of geojson
 * point features
 */
export const setTrailMarkerFeatures = (
  ...trailMarkerFeatures: Feature<Point>[]
) => {
  observableState.trailMarkerFeatures = trailMarkerFeatures.map((f) => f)
}

/**
 * Array of features for a geojson feature collection
 */
export const spotFeatures = () => observableState.spotFeatures

/**
 * Set the features for trail markers.  Takes variadic parameters of geojson
 * point features
 */
export const setSpotFeatures = (...spotFeatures: Feature<Point>[]) => {
  observableState.spotFeatures = spotFeatures.map((f) => f)
}

/**
 * Array of search pin features for a geojson feature collection
 */
export const searchPinFeatures = () => observableState.searchPinFeatures

/**
 * Set the features for search pin markers.
 */
export const setSearchPinFeatures = (searchPinFeatures: Feature<Point>[]) => {
  observableState.searchPinFeatures = [...searchPinFeatures]
}

/**
 * setSearchPinSelected allows us to set a specific search pin feature to the targeted state.
 * @param searchResultId The id of the search pin feature we want to add the selected_search state to.
 */
export const setSearchPinSelected = (searchResultId: string | null) =>
  (observableState.searchPinSelected = searchResultId
    ? {
        id: searchResultId,
        source: SEARCH_PIN_SOURCE,
      }
    : null)

/**
 * Returns an observable of search pin feature that is selected.
 *
 * @returns representing the search result feature that is selected
 */
export const searchPinSelected = () => observableState.searchPinSelected

/**
 * Array of features for a geojson feature collection
 */
export const trailMarkerFeatures = () => observableState.trailMarkerFeatures

/**
 * Cursor coord is the coordinates of the mouse position within the viewer
 * @return cursor geographic location
 */
export const cursorCoord = () => observableState.cursorCoord

/**
 * Cursor coord is the coordinates of the mouse position within the viewer
 * @param loc cursor geographic location
 */
export const setCursorCoord = (loc: Geographic) =>
  (observableState.cursorCoord = loc)

/**
 * Camera is the geographic position along with its orientation (pitch, bearing)
 * @return camera state object
 */
export const camera = () => observableState.camera

/**
 * Camera is the geographic position along with its orientation (pitch, bearing)
 * @param camera camera state object
 */
export const setCamera = (camera: Camera) => (observableState.camera = camera)

/**
 * Terrain exaggeration of the viewer
 * @return exaggeration
 */
export const terrainExaggeration = () => observableState.terrainExaggeration

/**
 * Set the terrain exaggeration of the viewer
 * @param value exaggeration value
 */
export const setTerrainExaggeration = (value: number) =>
  (observableState.terrainExaggeration = value)

/**
 * Destination represents the geographic location you wish the camera to animate to
 * @return location camera is destined for
 */
export const destination = () => observableState.destination

/**
 * Destination represents the geographic location you wish the camera to animate to
 * @param destinationlocation camera is destined for
 */
export const setDestination = (destination: Geographic | Camera) => {
  const height = observableState.camera?.location.height

  // determine if input is camera position or just geographic location
  if (destination instanceof Geographic) {
    // Use declared height or current height or default
    const newHeight = destination.height || height || 14
    const newDest = new Geographic(
      destination.longitude,
      destination.latitude,
      newHeight
    )

    const camera = new Camera({ location: newDest })
    observableState.destination = camera
  } else if (destination instanceof Camera) {
    observableState.destination = destination.clone()
  }
}

/**
 * Quick destination represents the geographic location you wish the camera to jump to (no easing)
 * @return location camera is destined for
 */
export const quickDestination = () => observableState.quickDestination

/**
 * Quick destination represents the geographic location you wish the camera to jump to (no easing)
 * @param destination - location camera is destined for
 */
export const setQuickDestination = (destination: Geographic | Camera) => {
  const height = observableState.camera?.location.height

  // determine if input is camera position or just geographic location
  if (destination instanceof Geographic) {
    // Use declared height or current height or default
    const newHeight = destination.height || height || 14
    const newDest = new Geographic(
      destination.longitude,
      destination.latitude,
      newHeight
    )

    const camera = new Camera({ location: newDest })
    observableState.quickDestination = camera
  } else if (destination instanceof Camera) {
    observableState.quickDestination = destination.clone()
  }
}

/**
 * Dealing with xgps legacy ids.
 * Will go away once xgps is no longer consumed.
 * Some layers end with '$' and are prefixes ... some end with '$' and are
 * NOT prefixes.
 */
const legacyIdToStylesheetIds = (id: string): string[] => {
  if (id.toString().endsWith('$')) {
    const ids = getLayersByIdPrefix([id])
    if (ids.length) {
      // Was prefix
      return [...ids]
    }
  }

  // Wasn't a prefix or didn't end with '$'
  return [id]
}

/**
 * Toggled layers is the list of all layers that have been interacted with (toggled on or off)
 * @returns object of all modified layers
 */
export const toggledLayers = () => observableState.toggledLayers

/**
 * Make layer visible and add to toggled layers object
 * @param layer object including 'id' and 'show' property
 */
export const showLayer = async (layer: LegacyLayer) => {
  await layerManagementReady()

  const id = layer.stylesheetID || layer.id

  const ids = legacyIdToStylesheetIds(id)
  showStyleLayers(ids)

  observableState.toggledLayers[id] = layer
  observableState.toggledLayers = {
    ...observableState.toggledLayers,
  }
}

/**
 * Make layer hidden and add to toggled layers object
 * @param layer object including 'id' and 'show' property
 */
export const hideLayer = async (layer: LegacyLayer) => {
  await layerManagementReady()

  const id = layer.stylesheetID || layer.id

  const ids = legacyIdToStylesheetIds(id)
  hideStyleLayers(ids)

  observableState.toggledLayers[id] = layer
  observableState.toggledLayers = {
    ...observableState.toggledLayers,
  }
}

/**
 * Set the opacity of a layer
 * @param layer object including 'id' and 'show' property
 */
export const setLayerOpacity = (layer: LegacyLayer, opacity: number) => {
  const id = layer.stylesheetID || layer.id

  const ids = legacyIdToStylesheetIds(id)
  setStyleLayerOpacities(ids, opacity)
}

/**
 * User coord is the coordinate of the user
 * Not set automatically
 * @return coordinate of user
 */
export const userCoord = () => observableState.userCoord

/**
 * Flag for determining if access to the geolocation api is enabled
 * Not set automatically
 * @return status of geolocation api
 */
export const geolocationEnabled = () => observableState.geolocationEnabled

/**
 * Helper function to pan map viewer to user's current location
 */
export const goToCurrentLocation = async () => {
  const defaultZoom = 11
  const userHeight = observableState.camera?.location.height

  // if no user coordinate, set one
  if (!observableState.userCoord) {
    await setUserCoord()
  }

  // if user height is less than default zoom, set to default zoom
  const zoom = userHeight && userHeight < defaultZoom ? defaultZoom : userHeight

  const newLocation = new Geographic(
    observableState.userCoord?.longitude,
    observableState.userCoord?.latitude,
    zoom
  )

  // copy current camera and update with user coord
  const newCamera = new Camera({
    ...observableState.camera,
    location: newLocation,
  })

  if (observableState.geolocationEnabled) {
    setDestination(newCamera)
  }

  return newCamera
}

/**
 * Private function that attempts to set the coordinates for the user
 */
export const setUserCoord = async () => {
  try {
    const { coords } = await getGeoLocation()
    const location = new Geographic(coords.longitude, coords.latitude)
    observableState.userCoord = location
    observableState.geolocationEnabled = true
  } catch (e) {
    observableState.geolocationEnabled = false
  }
}

/**
 * Private function to access the geolocation api
 */
const getGeoLocation = () => {
  return new Promise<GeolocationPosition>((resolve, reject) =>
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      timeout: 7000,
    })
  )
}

/**
 * Zoom is the change in zoom level from current
 * @return Change in zoom level (-1 or 1).
 */
export const userZoom = () => observableState.userZoom

/**
 * Zoom is the change in zoom level from current
 * @param deltaZoom Change in zoom level (-1 or 1).
 */
export const zoom = (deltaZoom: number) =>
  (observableState.userZoom = { deltaZoom })

/**
 * Put the map into query on click mode
 */
export const queryOnClick = () => {
  observableState.clickMode = QUERY_CLICK_MODE
}

/**
 * When in query on click mode we should query the coordinates clicked on the map
 * @returns wether we are in query on click mode
 */
export const inQueryOnClickMode = () =>
  observableState.clickMode === QUERY_CLICK_MODE

/**
 * Put the map into select on click mode
 */
export const selectMarkupOnClick = () => {
  observableState.clickMode = SELECT_MARKUP_CLICK_MODE
}

/**
 * When in select on click mode we should select any markup clicked in the map
 * @returns wether we are in select on click mode
 */
export const inSelectMarkupOnClickMode = () =>
  observableState.clickMode === SELECT_MARKUP_CLICK_MODE

/**
 * Put the map into select parcel on click mode
 */
export const selectParcelOnClick = () => {
  observableState.clickMode = SELECT_PARCEL_CLICK_MODE
}

/**
 * When in select on click mode we should select any parcel clicked in the map
 * @returns wether we are in select on click mode
 */
export const inSelectParcelOnClickMode = () => {
  return observableState.clickMode === SELECT_PARCEL_CLICK_MODE
}

/**
 * Updates with viewport to contain all features in view
 * @returns Object of arrays based on source
 */
export const featuresInView = () => observableState.featuresInView

/**
 * Updates with viewport to contain all features in view
 * @param featuresInView Object of arrays based on source
 */
export const setFeaturesInView = (featuresObj: Record<string, MapFeature[]>) =>
  (observableState.featuresInView = featuresObj)

/**
 * Updates with mouse move to contain all hovered features
 * @returns Array of features
 */
export const featuresHovered = () => observableState.featuresHovered

/**
 * Updates with mouse move to contain all hovered features
 * @param featureArr Array of features
 */
export const setFeaturesHovered = (featureArr: MapFeature[]) =>
  (observableState.featuresHovered = featureArr)

/**
 * Returns an observable of features that is filtered.
 * @returns representing the features that are filtered
 */
export const featuresFiltered = () => observableState.featuresFiltered

/**
 * The setFeaturesFiltered allows us to set multiple features to the filtered state.
 * The state must be defined in the stylesheet in order for the state to take effect.
 * @param featureArr - Array of features
 */
export const setFeaturesFiltered = (featureArr: MapFeature[]) =>
  (observableState.featuresFiltered = featureArr)

/**
 * Returns an observable of features that are favorited.
 * @returns representing the features that are favorited
 */
export const featuresFavorited = () => observableState.featuresFavorited

/**
 * The setFeaturesFavorited allows us to set multiple features to the favorited state.
 * The state must be defined in the stylesheet in order for the state to take effect.
 * @param featureArr - Array of features
 */
export const setFeaturesFavorited = (featureArr: MapFeature[]) =>
  (observableState.featuresFavorited = featureArr)

/**
 * Features found on the queryCoord location
 * @returns Array of features
 */
export const queryFeatures = () => observableState.queryFeatures

/**
 * Features found on the queryCoord location
 * @param featureArr - Array of features
 */
export const setQueryFeatures = (featureArr: MapFeature[]) =>
  (observableState.queryFeatures = featureArr)

/**
 * Features that are set to the highlighted source
 * @returns Array of features
 */
export const featuresHighlighted = () => observableState.featuresHighlighted

/**
 * Features that are set to the highlighted source
 * @param featureArr Array of features
 */
export const setFeaturesHighlighted = (featureArr: Feature[]) =>
  (observableState.featuresHighlighted = featureArr)

/**
 * Clear features that are set to the highlighted source
 */
export const clearFeaturesHighlighted = () =>
  (observableState.featuresHighlighted = [])

/**
 * The setFeatureSelected allows us to set a specific feature to the selected state.
 * The state must be defined in the stylesheet in order for the state to take
 * affect.
 *
 * The format of the feature update is as follows:
 *
 * {
 *    id: (number| string),
 *    source: (string)
 *    sourceLayer: (string)
 *
 * }
 *
 *
 * @param featureUpdate The feature we want to add the selected state to.
 */
export const setFeatureSelected = (featureUpdate: FeatureSelector | null) =>
  (observableState.featureSelected = featureUpdate)

/**
 * Returns an observable of features that is selected.
 *
 * @returns representing the feature that is selected
 */
export const featureSelected = () => observableState.featureSelected

/**
 * The setFeatureTargeted allows us to set a specific feature to the targeted state.
 * The state must be defined in the stylesheet in order for the state to take
 * affect.
 *
 * The format of the feature update is as follows:
 *
 * {
 *    id: (number| string),
 *    source: (string)
 *    sourceLayer: (string)
 *
 * }
 *
 *
 * @param featureUpdate The feature we want to add the targeted state to.
 */
export const setFeatureTargeted = (featureUpdate: FeatureSelector | null) =>
  (observableState.featureTargeted = featureUpdate)

/**
 * Returns an observable of features that is targeted.
 *
 * @returns representing the feature that is targeted
 */
export const featureTargeted = () => observableState.featureTargeted

/**
 * Query coord is the coordinates to check for features
 * @return cursor geographic location
 */
export const queryCoord = () => observableState.queryCoord

/**
 * Query coord is the coordinates to check for features
 * @param geoPoint cursor geographic location
 */
export const setQueryCoord = (geoPoint: Geographic) => {
  observableState.queryCoord = geoPoint
}

/**
 * Clear the query coord
 */
export const clearQueryCoord = () => (observableState.queryCoord = null)

/**
 * Orientates the camera to fit the feature
 * @returns feature to fit
 */
export const fitFeature = () => observableState.fitFeature

/**
 * Orientates the camera to fit the feature
 * @param  feature feature to fit
 */
export const setFitFeature = (feature: FitFeature) => {
  // clear feature to reset
  observableState.fitFeature = null
  observableState.fitFeature = feature
}

/**
 * Query marker coordinate
 * @returns geoJson array with query marker feature
 */
export const queryMarker = () => observableState.queryMarker

/**
 * Set query marker coordinate with Geographic object
 * @param arg0 geographic to place query marker
 */
export const setQueryMarkerCoord = ({ longitude, latitude }: Geographic) => {
  observableState.queryMarker = [point([longitude, latitude])]
}

/**
 * Set query marker coordinate to empty array to clear feature
 */
export const clearQueryMarker = () => (observableState.queryMarker = [])

/**
 * Updates on map-x-move to store visible region of the viewer
 * Initial value is set to the bounding box of the US as a polygon
 * @returns - visible region object
 */
export const visibleRegion = () => observableState.visibleRegion

/**
 * Updates on map-x-move to store visible region of the viewer
 * @param visibleRegionObj visible region object
 */
export const setVisibleRegion = (visibleRegionObj: Feature<Polygon>) =>
  (observableState.visibleRegion = visibleRegionObj)

/**
 * Orientates the camera to fit the bounds
 * @returns - bounds to fit
 */
export const fitBounds = () => observableState.fitBounds

/**
 * Orientates the camera to fit the bounds
 * @param bounds bounds to fit [[minLon, minLat], [maxLon, maxLat]]
 */
export const setFitBounds = (bounds: GeoPositionBounds) =>
  (observableState.fitBounds = bounds)

/**
 * Is a feature in view
 * @param feature A geojson feature
 */
export const isInView = (feature: Feature) =>
  booleanContains(visibleRegion(), feature)

/**
 * Is a point in view
 * @param geoPoint a point
 */
export const isPointInView = ({ latitude, longitude }: Geographic) =>
  isInView(point([longitude, latitude]))

/**
 * Returns if viewer currently in 3d
 */
export const viewer3d = () => observableState.viewer3d

/**
 * Sets if viewer is currently in 3d
 * @param is3d if viewer is in 3d
 */
export const setViewer3d = (is3d: boolean) => (observableState.viewer3d = is3d)

/**
 * Return string representation of viewer dimension
 * @returns '2d' or '3d'
 */
export const viewerDimension = () => (observableState.viewer3d ? '3d' : '2d')

/**
 * Returns map marker for scrubbing the x-elevation component
 */
export const elevationScrubMarker = () => observableState.elevationScrubMarker

/**
 * Sets map marker for scrubbing the x-elevation component
 * @param features elevation markers
 */
export const setElevationScrubMarker = (features: Feature[]) =>
  (observableState.elevationScrubMarker = features)

/**
 * Set the filters
 * @param filters of layer IDs and their filters
 */
export const setFilters = (filters: Map<string, unknown[]>) => {
  if (!filters) {
    return
  }

  setStyleLayerFilters(filters)
}

/**
 * Gets the current map cursor style
 * @returns Cursor style
 */
export const cursor = () => observableState.cursor

/**
 * Sets the map cursor to the specified cursor type
 * @param cursor Cursor type ('default', 'crosshair', 'move', 'grab', etc.)
 */
export const setCursor = (cursor: string) => (observableState.cursor = cursor)

/**
 * Resets the map cursor to default
 */
export function resetCursor() {
  setCursor('default')
}

/**
 * Returns if the map currently has pitch enabled
 * @returns Pitch enabled
 */
export const canPitch = () => observableState.canPitch

/**
 * Sets if the map currently has pitch enabled
 * @param canPitch if pitch is enabled
 */
export const setCanPitch = (canPitch: boolean) =>
  (observableState.canPitch = canPitch)

/**
 * Returns if the map currently has rotate enabled
 * @returns Rotate enabled
 */
export const canRotate = () => observableState.canRotate

/**
 * Sets if the map currently has rotate enabled
 * @param canRotate if rotate is enabled
 */
export const setCanRotate = (canRotate: boolean) =>
  (observableState.canRotate = canRotate)

/**
 * Set popup object for appending to map viewer
 * @param popup Pop up properties and template
 */
export const setPopup = (popup: Popup | null) => (observableState.popup = popup)

/**
 * Popup object currently appended to viewer
 * @returns Pop up properties and template
 */
export const popup = () => observableState.popup

/**
 * Add markers to map viewer
 * @param markers Markers properties and template
 */
export const addMarkers = (markers: Marker[]) => {
  observableState.markers = [...observableState.markers, ...markers]
}

/**
 * Remove markers from map viewer
 */
export const removeMarkers = (markers: Marker[]) => {
  const markerRemoveIds = markers.map((marker) => marker.id)

  observableState.markers = observableState.markers.filter(
    // filter out markers that are in markerRemoveIds array
    (stateMarker) => !markerRemoveIds.includes(stateMarker.id)
  )
}

/**
 * Remove all markers from map viewer
 */
export const clearAllMarkers = () => {
  removeMarkers(observableState.markers)
}

/**
 * Replace all markers in the map viewer
 * @param markers Markers properties and template
 */
export const replaceMarkers = (markers: Marker[]) => {
  clearAllMarkers()
  addMarkers(markers)
}

/**
 * Markers array currently appended to viewer
 * @returns Markers properties and template
 */
export const markers = () => observableState.markers

/**
 * Get images that can be displayed on map
 */
export const images = () => observableState.images

/**
 * Ideally map images would first be considered for the sprite sheet.
 * Add an image to the map style.
 * This image can be displayed on the map like any other icon.
 * @param id The ID of the image.
 * @param image The image
 * @param options Options object.
 */
export const addImage = (
  id: string,
  image: ImageParam,
  options: OptionsParams
) => {
  observableState.images.set(id, { image, options })
}

registerEventsOnIdle()

/**
 * Toggle 3d mode on/off
 */
export const toggle3d = () => {
  const currentCamera = camera()
  const pitch = viewer3d() ? 0 : 65
  const newCamera = new Camera({
    location: currentCamera?.location,
    pitch,
    bearing: currentCamera?.bearing,
  })
  setDestination(newCamera)
}
